import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type IAgentHierarchy, type IAgentHierarchyNode } from '../interface/peer-chat';
import { getAssignmentsByEntity } from '../lib/agent-assignment-api';
import { getMyHierarchy, getMyObserverHierarchy } from '../lib/agent-hierarchy-api';
import { hierarchyLevelOfCode, parentCodeOf } from '../lib/entity-code';
import { peerChatEnabled } from '../lib/peer-chat-flags';

export type AgentHierarchyStatus = 'idle' | 'loading' | 'ready' | 'forbidden' | 'unavailable' | 'error';

export interface IAgentHierarchyState {
	status: AgentHierarchyStatus;
	/** Where the answer came from. `by-entity` is the degraded path on an older api.core. */
	source: 'hierarchy' | 'by-entity' | null;
	self: IAgentHierarchyNode[];
	parents: IAgentHierarchyNode[];
	children: IAgentHierarchyNode[];
	electionId: number;
	refresh: () => void;
}

const EMPTY: IAgentHierarchy = { partyId: 0, electionId: 0, self: [], parents: [], children: [] };

/**
 * Who the signed-in agent may message: their direct parent(s) and direct children.
 *
 * <p>One read of api.core's `me/hierarchy`, computed by the same helper the server uses to
 * PROVE a relation before a thread opens — so the UI never draws a Message button the server
 * will then refuse. Refreshed on demand (a reassignment mid-election) rather than polled.</p>
 *
 * <p>On a 404 (an api.core that predates the route) it degrades for the PARENT only: trim the
 * agent's own code one segment and ask `by-entity` who covers it. Children are not derived that
 * way — a parent already pays the coverage list for them and this hook must not double it.</p>
 */
export const useAgentHierarchy = (
	electionId: number | undefined,
	ownCode: string | undefined,
	enabledFor: boolean,
	/**
	 * `observer`: an independent observer's links (their organisation's state observer and the
	 * observers it supervises). Never falls back to `by-entity`, which is a party coverage read.
	 */
	mode: 'agent' | 'observer' = 'agent'
): IAgentHierarchyState => {
	const enabled = enabledFor && !!electionId && electionId > 0 && peerChatEnabled();

	const [status, setStatus] = useState<AgentHierarchyStatus>('idle');
	const [source, setSource] = useState<IAgentHierarchyState['source']>(null);
	const [hierarchy, setHierarchy] = useState<IAgentHierarchy>(EMPTY);
	const inFlightRef = useRef(false);
	/** The own code the last answer was computed from; a different one means the fallback is stale. */
	const codeUsedRef = useRef<string | undefined>(undefined);

	const load = useCallback(async () => {
		if (!enabled || !electionId || inFlightRef.current) return;
		inFlightRef.current = true;
		codeUsedRef.current = ownCode;
		setStatus('loading');

		try {
			const outcome = mode === 'observer' ? await getMyObserverHierarchy(electionId) : await getMyHierarchy(electionId);

			if (!outcome.reachable) {
				setStatus('error');
				return;
			}
			if (outcome.httpStatus === 403) {
				setStatus('forbidden');
				return;
			}
			if (outcome.hierarchy) {
				setHierarchy(outcome.hierarchy);
				setSource('hierarchy');
				setStatus('ready');
				return;
			}
			if (outcome.httpStatus !== 404) {
				setStatus('error');
				return;
			}
			if (mode === 'observer') {
				setStatus('unavailable');
				return;
			}

			// Older api.core. The parent is one trim away; the children are the coverage list's job.
			const parentCode = parentCodeOf(ownCode);
			if (!parentCode) {
				setHierarchy({ ...EMPTY, electionId });
				setSource('by-entity');
				setStatus('ready');
				return;
			}

			const fallback = await getAssignmentsByEntity([parentCode], electionId);
			if (!fallback.reachable) {
				setStatus('error');
				return;
			}
			if (fallback.httpStatus === 403) {
				setStatus('forbidden');
				return;
			}
			if (fallback.httpStatus < 200 || fallback.httpStatus >= 300) {
				setStatus('unavailable');
				return;
			}

			const item = fallback.body?.data?.items?.find((i) => i.entityCode === parentCode);
			const agent = item?.isAssigned ? item.agent : null;
			const parents: IAgentHierarchyNode[] = agent
				? [
						{
							userId: agent.id,
							fullName: `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() || agent.userName || '',
							phoneNumber: agent.phoneNumber || null,
							entityCode: parentCode,
							level: hierarchyLevelOfCode(parentCode),
							resultType: item?.assignmentMeta?.resultType?.toLowerCase() ?? '',
							isActive: agent.isActive,
						},
					]
				: [];
			setHierarchy({ ...EMPTY, electionId, parents });
			setSource('by-entity');
			setStatus('ready');
		} finally {
			inFlightRef.current = false;
		}
	}, [enabled, electionId, ownCode, mode]);

	useEffect(() => {
		if (!enabled) {
			setStatus('idle');
			return;
		}
		// `load` changes identity with `ownCode`; a code that arrives after a 404 fallback ran
		// without one must re-run it, and a code that has not changed must not.
		if (inFlightRef.current && codeUsedRef.current === ownCode) return;
		void load();
	}, [enabled, load, ownCode]);

	const refresh = useCallback(() => {
		void load();
	}, [load]);

	return useMemo(
		() => ({
			status,
			source,
			self: hierarchy.self,
			parents: hierarchy.parents,
			children: hierarchy.children,
			electionId: hierarchy.electionId,
			refresh,
		}),
		[status, source, hierarchy, refresh]
	);
};
